import { CreateProductBundleInput } from '@features/catalog/models/product-input.models';
import { ProductFormChangeSnapshot } from '@features/catalog/models/product-form-draft.models';
import { ProductStructure } from '@features/catalog/models/product.models';
import { mapModifierGroupsToStructureSnapshot } from '@features/catalog/utils/product-form-modifier-draft.mapper';
import { valuesEqual } from '@features/catalog/utils/form-change-tracker';

/** Con una sola dimensión de variación no hay atributos "secundarios" respecto al agrupamiento. */
export function usesPrimarySecondaryVariantSplit(variationEligibleGroupCount: number): boolean {
  return variationEligibleGroupCount > 1;
}

/** Producto con variaciones en BD debe editarse como variable aunque `product_structure` venga simple. */
export function inferProductStructureFromProduct(
  productStructure: ProductStructure | null | undefined,
  variantCount: number
): 'simple' | 'variable' {
  if (productStructure === 'variable' || variantCount > 0) {
    return 'variable';
  }
  return 'simple';
}

/** Backend infiere estructura por `variants.present?`; en simple debe ir `[]`. */
export function bundleVariantsForProductStructure<T>(
  productStructure: 'simple' | 'variable',
  mappedVariants: readonly T[]
): T[] {
  return productStructure === 'variable' ? [...mappedVariants] : [];
}

export function shouldUseProductBundleUpdate(isEdit: boolean, productId: string | null): boolean {
  return isEdit && Boolean(productId?.trim());
}

function bundleStructureSnapshot(snapshot: ProductFormChangeSnapshot): unknown {
  return {
    productStructure: snapshot.form.productStructure,
    attributeGroups: snapshot.attributeGroups,
    variantCombinations: snapshot.variants.map((variant) => ({
      id: variant.id,
      isNew: variant.isNew,
      attributeValues: variant.attributeValues.map((value) => ({
        definitionId: value.definitionId,
        definitionName: value.definitionName,
        value: value.value,
      })),
    })),
    modifierGroups: mapModifierGroupsToStructureSnapshot(snapshot.modifierGroups),
  };
}

export function hasProductBundleStructureChanges(
  baseline: ProductFormChangeSnapshot,
  current: ProductFormChangeSnapshot
): boolean {
  return !valuesEqual(bundleStructureSnapshot(baseline), bundleStructureSnapshot(current));
}

/** Al desactivar el producto, ningún canal de venta queda visible. */
export function resolveProductPublicationFlags(
  active: boolean,
  publicVisible: boolean,
  posVisible: boolean
): { active: boolean; publicVisible: boolean; posVisible: boolean } {
  if (!active) {
    return { active: false, publicVisible: false, posVisible: false };
  }
  return { active: true, publicVisible, posVisible };
}

export function omitUnchangedBundleStructureFields(
  input: CreateProductBundleInput,
): CreateProductBundleInput {
  const {
    attributes: _attributes,
    variants: _variants,
    modifierGroups: _modifierGroups,
    price,
    ...rest
  } = input;
  // Variable manda price=null; no enviarlo en updates parciales o pisa un producto simple en BE.
  if (price == null) {
    return rest;
  }
  return { ...rest, price };
}

/**
 * Updates parciales: no quitar variantes si el formulario es variable y hay filas.
 * Simple siempre envía `variants: []` y `attributes: []` para desactivar huérfanas
 * (p. ej. import Woo con `product_structure=simple` y filas/atributos activos);
 * si se omiten las claves, el BE no las toca.
 */
export function shouldOmitBundleStructureFieldsOnUpdate(
  hasStructureChanges: boolean,
  productStructure: 'simple' | 'variable',
  variantCount: number
): boolean {
  if (productStructure === 'simple') {
    return false;
  }
  if (hasStructureChanges) {
    return false;
  }
  if (productStructure === 'variable' && variantCount > 0) {
    return false;
  }
  return true;
}
